import type { CatalogApp } from "@cheesy/contracts";
import { ArrowUpRight, Boxes, Camera, Clipboard, Ellipsis, MapPin, Maximize2 } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { relativeDate } from "@/lib/utils";
import { useMobileInterface } from "@/lib/mobile";

const iconMap = [Boxes, Clipboard, Camera, MapPin];

export function AppCard({ app, index = 0 }: { app: CatalogApp; index?: number }) {
  const mobile = useMobileInterface();
  const Icon = iconMap[index % iconMap.length];
  return (
    <Card className="group relative overflow-hidden transition duration-200 hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-lg focus-within:border-primary/55 focus-within:shadow-lg">
      <Link to={`/apps/${app.slug}/launch`} className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" aria-label={`Open ${app.name}`} />
      <div className="pointer-events-none relative p-5">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[inset_0_-2px_0_rgb(0_51_117_/_35%)]">
            <Icon className="size-6" />
          </div>
          <ArrowUpRight className="size-5 text-muted-foreground transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg">{app.name}</h2>
          <Link
            to={`/apps/${app.slug}`}
            className="touch-target pointer-events-auto relative z-10 -m-2 inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={`View details and management for ${app.name}`}
            title="App details and management"
          >
            <Ellipsis className="size-5" />
          </Link>
        </div>
        <p className="mt-2 min-h-11 text-sm leading-relaxed text-muted-foreground">{app.summary}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {mobile && !app.mobileSupported && <Badge variant="warning">Desktop app</Badge>}
          {app.categories.slice(0, 2).map((category) => <Badge key={category} variant="secondary">{category}</Badge>)}
          {app.permissions.fullscreen && <Badge variant="outline"><Maximize2 className="mr-1 size-3" /> Fullscreen</Badge>}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
          <span>{app.owners.join(", ")}</span>
          <span>Updated {relativeDate(app.updatedAt)}</span>
        </div>
      </div>
    </Card>
  );
}
